import { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { deepmerge } from '@mui/utils';
import { normalizeThemePreference, resolveThemeMode, resolveThemeOverrides } from './theme-preference';
import { SGSThemeProvider } from './theme-provider';

import type { ReactNode } from 'react';
import type { ThemeOptions } from '@mui/material/styles';
import type { ThemeMode } from './theme-vars';
import type { ThemePreference } from './theme-preference';

type ThemePreferenceValue = {
  preference: ThemePreference;
  mode: ThemeMode;
  setPreference: (value: string | null | undefined) => void;
};

const storageKey = 'themePreference';

const inertThemePreference: ThemePreferenceValue = {
  preference: 'default',
  mode: 'default',
  setPreference: () => undefined,
};

const ThemePreferenceContext = createContext<ThemePreferenceValue>(inertThemePreference);

type ThemePreferencesProps = {
  children: ReactNode;
  themeOverrides?: ThemeOptions;
};

function readStoredPreference(): ThemePreference {
  let stored: ThemePreference = 'default';
  if (typeof window !== 'undefined') {
    try {
      stored = normalizeThemePreference(window.localStorage.getItem(storageKey));
    } catch {} // Storage access can throw in private modes / restricted contexts
  }
  return stored;
}

export function ThemePreferences({ children, themeOverrides }: ThemePreferencesProps) {
  const [preference, setStoredPreference] = useState<ThemePreference>(readStoredPreference);

  useEffect(() => {
    const handleStorage = (event: StorageEvent) => {
      if (event.key === storageKey) {
        setStoredPreference(normalizeThemePreference(event.newValue));
      }
    };
    window.addEventListener('storage', handleStorage);
    return () => {
      window.removeEventListener('storage', handleStorage);
    };
  }, []);

  const setPreference = useCallback((value: string | null | undefined) => {
    const next = normalizeThemePreference(value);
    setStoredPreference(next);
    try {
      window.localStorage.setItem(storageKey, next);
    } catch {} // Storage access can throw in private modes / restricted contexts
  }, []);

  const mode = resolveThemeMode(preference);

  const overrides = useMemo(() => {
    let result = resolveThemeOverrides(preference);
    if (themeOverrides) {
      result = deepmerge(result, themeOverrides);
    }
    return result;
  }, [preference, themeOverrides]);

  const value = useMemo<ThemePreferenceValue>(
    () => ({ preference: preference, mode: mode, setPreference: setPreference }),
    [preference, mode, setPreference]
  );

  return (
    <ThemePreferenceContext.Provider value={value}>
      <SGSThemeProvider mode={mode} themeOverrides={overrides}>
        {children}
      </SGSThemeProvider>
    </ThemePreferenceContext.Provider>
  );
}

export function useThemePreference(): ThemePreferenceValue {
  return useContext(ThemePreferenceContext);
}
